import { sha256 } from 'js-sha256'

import { fetchBinFile } from 'api'

export const filterInvalidDomains = (arr: string[]): string[] => {
  return arr.filter((i) => /^https?:\/\//.test(i))
}

interface FastestOriginOptions {
  timeoutMs?: number
}

export const getDataValUrl = (origin: string): string => {
  return `${origin.replace(/\/$/, '')}/data_val`
}

/* 傳入多個測速網址，優先回傳最快通過 hostname checksum 驗證的網址。
優先順序：
1. 若有網址通過 checksum 驗證，回傳最快通過驗證的網址。
2. 若沒有網址通過 checksum，退回使用最快成功回應的網址。
3. 若完全沒有成功回應，退回第一個網址。
當網址回傳時間timeout，會將其視為回傳失敗
*/
export async function getFastestOrigin(urls: string[], options: FastestOriginOptions = {}): Promise<string> {
  const { timeoutMs = 5000 } = options
  const getResponseText = (response: any): string => {
    const data = response?.data ?? response
    if (typeof data === 'string') return data
    if (data instanceof ArrayBuffer) return new TextDecoder().decode(data)
    return JSON.stringify(data) ?? ''
  }

  const validUrls = filterInvalidDomains(urls)
  const respondedUrls: string[] = []
  const unavailableUrls = new Set<string>()

  // Case 3: 若沒有任何網址成功回應，退回使用第一個網址。
  const fallbackToFirstUrl = (error?: unknown): string => {
    const firstUrl = validUrls[0] ?? urls[0]
    if (!firstUrl) {
      throw Object.assign(new Error('请检查网络设置'), { title: '网络异常' })
    }

    console.warn(`[origin test] no http 200 response, fallback to first url: ${firstUrl}`, error)
    return firstUrl
  }

  // Case 2: 若沒有網址通過 checksum，退回使用最快成功回應的網址。
  const fallbackToFastestRespondedOrFirst = (error?: unknown): string => {
    const fastestRespondedUrl = respondedUrls[0]
    if (fastestRespondedUrl) {
      return fastestRespondedUrl
    }

    return fallbackToFirstUrl(error)
  }

  const probeUrl = async (url: string): Promise<{ url: string; hasExpectedChecksum: boolean }> => {
    const controller = new AbortController()
    let isTimeout = false
    const timeoutId = setTimeout(() => {
      isTimeout = true
      unavailableUrls.add(url)
      controller.abort()
    }, timeoutMs)

    let response: any
    try {
      response = await fetchBinFile({ url, signal: controller.signal })
      // axios 只有 2xx response 會 resolve，因此這裡紀錄的是成功回應順序。
      respondedUrls.push(url)
    } catch (error) {
      unavailableUrls.add(url)
      if (isTimeout) {
        console.warn(`[origin test] request timeout: ${url}`)
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
    }

    const hostname = new URL(url).hostname
    const expectedChecksum = sha256(hostname).slice(0, 30)

    return {
      url,
      hasExpectedChecksum: getResponseText(response).includes(expectedChecksum),
    }
  }

  // 同時探測所有網址。
  const probes = validUrls.map(probeUrl)
  const checksumMatchedUrls = probes.map((probe) =>
    probe.then(({ url, hasExpectedChecksum }) => {
      if (unavailableUrls.has(url)) {
        throw new Error(`[origin test] unavailable url: ${url}`)
      }
      // 讓不符合 checksum 的成功回應進入 Promise.any 的 reject 流程，繼續等待其他網址。
      if (!hasExpectedChecksum) {
        throw new Error(`invalid url checksum: ${url}`)
      }
      // console.log('[origin test] passed checksum validation:', url)

      // Case 1: 符合 checksum 驗證。
      return url
    })
  )

  const fastestUrl = await Promise.any(checksumMatchedUrls).catch((err) => {
    // 若沒有任何網址通過驗證，退回使用最快成功回應的網址。
    return fallbackToFastestRespondedOrFirst(err)
  })

  return fastestUrl
}

export async function getFastestOriginWithDataVal(
  origins: string[],
  options: FastestOriginOptions = {}
): Promise<string> {
  const originByDataValUrl = new Map<string, string>()
  const dataValUrls = filterInvalidDomains(origins).map((origin) => {
    const dataValUrl = getDataValUrl(origin)
    originByDataValUrl.set(dataValUrl, origin)
    return dataValUrl
  })
  const fastestDataValUrl = await getFastestOrigin(dataValUrls, options)

  return originByDataValUrl.get(fastestDataValUrl) ?? fastestDataValUrl
}
